// The Answer & Survey Results table for one question.
//
// A table in place of the prose list of answers: one row per
// student, showing what they said in Solo Time, in Team Time Round 1 and in
// Round 2, alongside the confidence they reported before starting.
//
// Rounds come from the challenge row, which now carries every attempt tagged
// with its round rather than only the student's last answer.

import { CHALLENGE_SURVEY_OPTIONS } from "utils/general";

const SOLO = "solo";
const TEAM = "team";

// What a response reads as once its emoji, case and punctuation are gone —
// the parts of the copy the model reliably mangles when it retells a survey
// answer.
const surveyEssence = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// The canonical option copy, by enum value ("EASY") and by de-emojied label,
// so both of the model's retellings resolve to the copy the student actually
// tapped — emoji included.
const SURVEY_LABELS = CHALLENGE_SURVEY_OPTIONS.reduce((byKey, option) => {
  byKey[option.value.toLowerCase()] = option.label;
  byKey[surveyEssence(option.label)] = option.label;
  // Retellings of earlier copy still resolve to the current canonical label.
  (option.legacyLabels || []).forEach((legacy) => {
    byKey[surveyEssence(legacy)] = option.label;
  });
  return byKey;
}, {});

/**
 * The survey answer as the student saw it.
 *
 * The report's survey column comes from the model's retelling, which drops
 * the emoji or reduces the answer to its enum value. Anything recognisable as
 * one of the fixed options becomes that option's own copy; anything else is
 * shown as given rather than guessed at.
 *
 * @param {string|null|undefined} response
 * @returns {string|null}
 */
export const canonicalSurveyLabel = (response) => {
  if (!response || typeof response !== "string") return response ?? null;
  return SURVEY_LABELS[surveyEssence(response)] || response;
};

// A student may answer the same round more than once. Their last attempt in it
// is the one they settled on — the same reading `consensusRound` takes, so the
// table and the consensus verdict cannot disagree about the same student.
// Rounds arrive oldest first and carry no timestamp or final marker, so order
// is the only signal there is.
const answerFor = (rounds = [], type, number) => {
  const inRound = rounds.filter(
    (r) => r?.round_type === type && (number === undefined || r?.round_number === number),
  );
  const match = inRound[inRound.length - 1];
  return match ? { answer: match.answer, isCorrect: match.is_correct } : null;
};

// Team rounds are read by their own number, never by position. Position cannot
// tell a second attempt at Round 1 apart from a Round 2, and reading it that
// way filled the Round 2 column with a round that never happened.
const teamAnswer = (rounds = [], index) => answerFor(rounds, TEAM, index + 1);

/**
 * Build the table's rows for one question.
 *
 * @param {Array<object>} submissions The question's `submissions`, each with
 *   `student_name`, `rounds`, and optional `not_applicable`, `outcome_reason`,
 *   and `not_applicable_reason`.
 * @param {Object<string, string>} surveyByStudent Confidence answer keyed by
 *   lowercased student name.
 * @returns {Array<{studentName: string, survey: string|null,
 *   solo: object|null, teamRound1: object|null, teamRound2: object|null,
 *   outcomeReason: string|null, notApplicable: boolean|null,
 *   notApplicableReason: string|null}>}
 *   Empty when no student answered in legacy sessions.
 */
export const answerSurveyRows = (submissions = [], surveyByStudent = {}) => {
  if (!Array.isArray(submissions) || submissions.length === 0) return [];

  return submissions
    .map((submission) => {
      const studentName = submission?.student_name || "Unknown";
      const rounds = submission?.rounds || [];
      const outcomeReason = submission?.outcome_reason ?? null;
      const notApplicable = submission?.not_applicable ?? null;
      const notApplicableReason = submission?.not_applicable_reason ?? null;

      let solo = answerFor(rounds, SOLO);
      if (!solo) {
        if (notApplicable === true) {
          solo = { answer: "N/A", isCorrect: null };
        } else if (notApplicable === false) {
          solo = { answer: "", isCorrect: false };
        }
      }

      return {
        studentName,
        // The submission's own survey_response is the event the student's tap
        // emitted; the LLM's surveyResults stands in for reports served before
        // the backend carried it.
        survey: canonicalSurveyLabel(submission?.survey_response)
          || surveyByStudent[studentName.toLowerCase()]
          || null,
        solo,
        teamRound1: teamAnswer(rounds, 0),
        teamRound2: teamAnswer(rounds, 1),
        outcomeReason,
        notApplicable,
        notApplicableReason,
      };
    })
    .filter(
      (row) =>
        row.solo ||
        row.teamRound1 ||
        row.teamRound2 ||
        row.survey ||
        row.notApplicable !== null ||
        row.outcomeReason !== null,
    );
};

/**
 * Key the report's per-question survey results by student name.
 *
 * Entries arrive one per student (`{studentName, response}`); older payloads
 * group students under a shared rating instead, so both shapes are read.
 *
 * @param {Array<object>} surveyResults
 * @returns {Object<string, string>} Keyed by lowercased student name.
 */
export const surveyByStudentName = (surveyResults = []) => {
  const byName = {};
  if (!Array.isArray(surveyResults)) return byName;

  surveyResults.forEach((entry) => {
    if (!entry) return;

    const name = entry.studentName || entry.student_name || entry.student;
    const response = canonicalSurveyLabel(entry.response || entry.rating || entry.answer);
    if (name && response) {
      byName[String(name).toLowerCase()] = response;
      return;
    }

    const students = entry.students || entry.studentNames;
    if (response && Array.isArray(students)) {
      students.forEach((student) => {
        if (student) byName[String(student).toLowerCase()] = response;
      });
    }
  });
  return byName;
};
